import React, {useContext, useMemo} from 'react';
import './SearchableSelectBox.scss';
import {Autocomplete, TextField} from '@mui/material';
import {ThemeProvider} from "@mui/material/styles";
import {InputTheme} from "../InputTheme";
import {filter, find} from "lodash";
import {I18nReactContext} from "../../../contexts/I18nContext";

/**
 * A select box the user can type in to filter the options by name.
 * Takes the same props as SelectBox, so it can replace it where the list is long.
 * Matching is case-insensitive and matches any part of the option text.
 *
 * @param {object} props - The component props
 * @param {string} [props.className] - Additional CSS class name for the container
 * @param {string} props.label - The label text for the input
 * @param {string|number} props.value - The currently selected value ('' or undefined for none)
 * @param {function} props.onChange - Called with an event-like object ({target: {value}}) when the selection changes.
 *                                    The value is '' when the user clears the selection.
 * @param {Array<{value: string|number, text?: string, textKey?: string, disabled?: boolean}>} props.options - Options to show.
 *                                    Options with an empty value are skipped (they are the "nothing selected" row of a native select).
 * @param {boolean} [props.disabled] - Whether the input is disabled
 * @param {string} [props.dataTestId] - Test ID for the text input
 * @returns {JSX.Element} A Material-UI Autocomplete wrapped in a ThemeProvider
 */
export const SearchableSelectBox = ({className = '', label, value, onChange, options, disabled, dataTestId}) => {
    const {i18n} = useContext(I18nReactContext);

    const selectableOptions = useMemo(
        () => filter(options, (option) => option.value !== '' && option.value !== undefined && option.value !== null),
        [options]
    );
    const selectedOption = find(selectableOptions, {value}) || null;

    /**
     * Returns the text shown for an option.
     * @param {object} option - The option
     * @returns {string} The option text
     */
    const getOptionText = (option) => option?.text || (option?.textKey ? String(i18n.translate(option.textKey)) : '');

    /**
     * Passes the new value up in the same shape SelectBox uses.
     * @param {object} _event - The Autocomplete change event (not used)
     * @param {object|null} option - The chosen option, or null when cleared
     */
    const handleChange = (_event, option) => {
        onChange({target: {value: option ? option.value : ''}});
    };

    return <ThemeProvider theme={InputTheme}>
        <div className={`select-box searchable-select-box ${className}`}>
            <Autocomplete
                options={selectableOptions}
                value={selectedOption}
                onChange={handleChange}
                disabled={disabled}
                getOptionLabel={getOptionText}
                getOptionDisabled={(option) => !!option.disabled}
                isOptionEqualToValue={(option, selected) => option.value === selected.value}
                noOptionsText={String(i18n.translate('autoComplete.noOptions'))}
                slotProps={{paper: {className: 'searchable-select-box__paper'}}}
                renderOption={(props, option) => {
                    const {key, ...optionProps} = props;
                    return <li key={option.value} {...optionProps}>{getOptionText(option)}</li>;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={String(i18n.translate('autoComplete.search'))}
                        inputProps={{...params.inputProps, 'data-testid': dataTestId}}
                    />
                )}
            />
        </div>
    </ThemeProvider>
}
